import { useRef, useState } from "react";
import type { Hint } from "../game/kitchen";
import type { Controls } from "../play/controls";

// Phone controls: the left side of the screen is a stick that appears where
// your thumb lands, a drag anywhere else looks around, and the Use button
// (labelled with what it'll do) sits under the right thumb. Hold it to keep
// scrubbing.

const STICK_RADIUS = 48;

export function TouchControls({ controls, hint }: { controls: Controls; hint: Hint | null }) {
  const stickId = useRef<number | null>(null);
  const lookId = useRef<number | null>(null);
  const last = useRef({ x: 0, y: 0 });
  const [stick, setStick] = useState<{ ox: number; oy: number; dx: number; dy: number } | null>(null);

  const onDown = (e: React.PointerEvent) => {
    const leftSide = e.clientX < window.innerWidth * 0.45;
    if (leftSide && stickId.current === null) {
      stickId.current = e.pointerId;
      setStick({ ox: e.clientX, oy: e.clientY, dx: 0, dy: 0 });
    } else if (lookId.current === null) {
      lookId.current = e.pointerId;
      last.current = { x: e.clientX, y: e.clientY };
    } else return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const onMove = (e: React.PointerEvent) => {
    if (e.pointerId === stickId.current && stick) {
      let dx = e.clientX - stick.ox;
      let dy = e.clientY - stick.oy;
      const len = Math.hypot(dx, dy);
      if (len > STICK_RADIUS) {
        dx = (dx / len) * STICK_RADIUS;
        dy = (dy / len) * STICK_RADIUS;
      }
      controls.stick = { x: dx / STICK_RADIUS, y: -dy / STICK_RADIUS };
      setStick({ ...stick, dx, dy });
    } else if (e.pointerId === lookId.current) {
      controls.touchLook(e.clientX - last.current.x, e.clientY - last.current.y);
      last.current = { x: e.clientX, y: e.clientY };
    }
  };

  const onUp = (e: React.PointerEvent) => {
    if (e.pointerId === stickId.current) {
      stickId.current = null;
      controls.stick = { x: 0, y: 0 };
      setStick(null);
    } else if (e.pointerId === lookId.current) lookId.current = null;
  };

  const canAct = hint?.action != null;
  return (
    <div
      className="absolute inset-0 touch-none select-none"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      {stick ? (
        <div className="pointer-events-none absolute" style={{ left: stick.ox - STICK_RADIUS, top: stick.oy - STICK_RADIUS }} aria-hidden="true">
          <div className="size-24 rounded-full border-2 border-panel/50 bg-ink/25" />
          <div
            className="absolute top-1/2 left-1/2 size-11 rounded-full bg-panel/80 shadow"
            style={{ transform: `translate(calc(-50% + ${stick.dx}px), calc(-50% + ${stick.dy}px))` }}
          />
        </div>
      ) : (
        <p className="pointer-events-none absolute bottom-[calc(env(safe-area-inset-bottom,0px)+2.5rem)] left-6 rounded-full bg-ink/50 px-3 py-1 text-xs font-bold text-panel">
          Thumb here to walk · drag to look
        </p>
      )}
      <button
        type="button"
        onPointerDown={(e) => {
          e.stopPropagation();
          controls.pressUse();
          controls.touchUseHeld = true;
        }}
        onPointerUp={() => (controls.touchUseHeld = false)}
        onPointerCancel={() => (controls.touchUseHeld = false)}
        onPointerLeave={() => (controls.touchUseHeld = false)}
        className={`absolute right-4 bottom-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] flex size-28 items-center justify-center rounded-full p-3 text-center text-sm leading-tight font-black shadow-lg ${
          canAct ? "bg-sunny text-ink active:scale-95" : "bg-ink/60 text-panel/80"
        }`}
      >
        {canAct ? hint!.label : "Use"}
      </button>
    </div>
  );
}
